
import Chart from 'chart.js/auto';
window.Chart = Chart;

let chartInstance = null;
const stateColors = {};             // Keeps track of each state's assigned color
const usedColors = new Set();       // Tracks which colors are actively used
const predefinedColors = [
    '#0A986D',   // green
    '#2d95e5',   // light blue
    '#FFD039',   // yellow
    '#FC8D28',   // orange
    '#C0392B',   // red
    '#2862a4'    // muted blue
];

export const renderCustomRangeChart = (startMonth, endMonth, selectedStates) => {
    const chartCanvas = document.getElementById("customRangeChart");
    if (!chartCanvas || !window.months_data) return;

    const monthKeys = Object.keys(window.months_data)
        .filter(label => {
            const monthYear = window.months_data[label].month_year;
            return monthYear >= startMonth && monthYear <= endMonth;
        })
        .sort((a, b) => {
            return window.months_data[a].month_year.localeCompare(window.months_data[b].month_year);
        });

    const datasetsMap = {};

    monthKeys.forEach(monthKey => {
        const monthData = window.months_data[monthKey];
        const readableMonth = monthKey.replace(/([a-zA-Z]+)(\d{4})/, "$1 $2");

        monthData.jurisdiction_data.forEach(entry => {
            const state = entry.state;
            if (!selectedStates.includes(state)) return;


            // reset and track used colors
            usedColors.clear();
            selectedStates.forEach(state => {
                if (stateColors[state]) {
                    usedColors.add(stateColors[state]);
                }
            });

            // reassign new colors only if not already assigned
            selectedStates.forEach(state => {
                if (!stateColors[state]) {
                    const availableColor = predefinedColors.find(color => !usedColors.has(color));
                    stateColors[state] = availableColor;
                    usedColors.add(availableColor);
                }
            });

            //clean up colors for states that are no longer selected
            Object.keys(stateColors).forEach(state => {
                if (!selectedStates.includes(state)) {
                    delete stateColors[state];
                }
            });

            if (!datasetsMap[state]) {
                datasetsMap[state] = {
                    label: state,
                    data: [],
                    borderColor: stateColors[state],
                    backgroundColor: stateColors[state],
                    borderWidth: 2,
                    tension: 0.3
                };
            }

            const newBusinesses = parseInt(entry.new_businesses?.replace(/,/g, ""), 10) || 0;
            datasetsMap[state].data.push({
                x: readableMonth,
                y: newBusinesses
            });
        });
    });

    const datasets = selectedStates
        .filter(state => datasetsMap[state])
        .map(state => datasetsMap[state]);

    if (chartInstance) {
        chartInstance.destroy();
    }

    chartInstance = new Chart(chartCanvas, {
        type: 'line',
        data: { datasets },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: { display: false },
                title: {
                    display: true,
                    text: 'New Businesses Over Time'
                }
            },
            scales: {
                x: {
                    type: 'category',
                    ticks: {
                        callback: function(value, index, ticks) {
                            const rawLabel = this.getLabelForValue(value);
                            const [month, year] = rawLabel.split(" ");
                            return `${month.slice(0, 3)} ${year}`;
                        }
                    }
                },
                y: {
                    beginAtZero: true,
                    title: { display: true, text: 'New Businesses' }
                }
            }
        }
    });
    chartInstance.resize();
    chartInstance.update();
    updateSelectedStateList(datasets);
};


export function setupRangeChartUI() {
    const startSelect = document.getElementById("range-start");
    const endSelect = document.getElementById("range-end");
    const checkboxContainer = document.getElementById("state-checkboxes");

    if (!startSelect || !endSelect || !checkboxContainer || !window.months_data) return;

    const sortedKeys = Object.keys(window.months_data).sort((a, b) => {
        const dateA = new Date(window.months_data[a].month_year);
        const dateB = new Date(window.months_data[b].month_year);
        return dateA - dateB;
    });

    sortedKeys.forEach(label => {
        const monthYear = window.months_data[label].month_year;
        const option = document.createElement("option");
        option.value = monthYear;
        const fullLabel = label.replace(/([a-zA-Z]+)(\d{4})/, "$1 $2");
        const [month, year] = fullLabel.split(" ");
        const abbreviated = `${month.slice(0, 3)} ${year}`;
        option.textContent = abbreviated;


        startSelect.appendChild(option.cloneNode(true));
        endSelect.appendChild(option);
    });

    const first = window.months_data[sortedKeys[0]].month_year;
    const last = window.months_data[sortedKeys.at(-1)].month_year;
    startSelect.value = first;
    endSelect.value = last;

    const allStates = new Set();
    sortedKeys.forEach(key => {
        window.months_data[key].jurisdiction_data.forEach(entry => {
            if (entry.state) allStates.add(entry.state);
        });
    });

    const sortedStates = Array.from(allStates).sort();

    sortedStates.forEach(state => {
        const label = document.createElement("label");
        label.style.display = "flex";
        label.style.alignItems = "center";
        label.style.gap = "0.5rem";

        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.value = state;
        checkbox.name = "state-filter";

        label.appendChild(checkbox);
        label.appendChild(document.createTextNode(state));
        checkboxContainer.appendChild(label);
    });

    const updateChart = (event) => {
        const checkboxes = document.querySelectorAll('input[name="state-filter"]');
        const selected = Array.from(checkboxes).filter(cb => cb.checked);

        if (selected.length > 6) {
            alert("Please select up to 6 states only.");

            // Uncheck the one that triggered the event
            if (event && event.target && event.target.checked) {
                event.target.checked = false;
            }

            return;
        }

        const selectedStates = selected.map(cb => cb.value);
        renderCustomRangeChart(startSelect.value, endSelect.value, selectedStates);
    };



    checkboxContainer.addEventListener("change", updateChart);
    startSelect.addEventListener("change", updateChart);
    endSelect.addEventListener("change", updateChart);

    const defaultStates = sortedStates.slice(0, 6);
    document.querySelectorAll('input[name="state-filter"]').forEach(cb => {
        if (defaultStates.includes(cb.value)) cb.checked = true;
    });

    renderCustomRangeChart(first, last, defaultStates);
}

function updateSelectedStateList(datasets) {
    const container = document.getElementById("selected-state-list");
    if (!container) return;

    container.innerHTML = "";

    datasets.forEach(dataset => {
        const item = document.createElement("li");
        item.style.display = "flex";
        item.style.alignItems = "center";
        item.style.gap = "0.5rem";
        item.style.marginBottom = "0.25rem";

        const colorLine = document.createElement("span");
        colorLine.style.display = "inline-block";
        colorLine.style.width = "24px";
        colorLine.style.height = "3px";
        colorLine.style.backgroundColor = dataset.borderColor || "#000";
        colorLine.style.borderRadius = "2px";

        const label = document.createElement("span");
        label.textContent = dataset.label;

        item.appendChild(colorLine);
        item.appendChild(label);
        container.appendChild(item);
    });
}

window.addEventListener('resize', () => {
    if (chartInstance) {
        chartInstance.resize();
    }
});